/* eslint-disable @next/next/inline-script-id */
import { useEffect } from 'react'

import Head from 'next/head'
import Script from 'next/script'

export default function TngPage({ title, mfeOther }) {
  useEffect(() => {
    const misoApiUrl = process.env.NEXT_PUBLIC_MISO_API_URL
    const onExitInterActionDataToSend = []
    const timeOnPageStart = new Date()

    function isTrackingBlocked() {
      return navigator.globalPrivacyControl || document.cookie.includes('EnableGpc=')
    }

    function isInternalCustomer() {
      return Boolean(window.SMI?.isCustomerInternal || window.isCustomerInternal)
    }

    function cookieReader(cookieName) {
      const name = `${cookieName}=`
      const splitCookies = document.cookie.split(';')

      for (let i = 0; i < splitCookies.length; i++) {
        let cookie = splitCookies[i]
        while (cookie.charAt(0) === ' ') {
          cookie = cookie.substring(1)
        }
        if (cookie.indexOf(name) === 0) {
          return cookie.substring(name.length, cookie.length)
        }
      }

      return ''
    }

    function getGuid() {
      return cookieReader('WebUserGuid')
    }

    function getVin() {
      return cookieReader('VIN')
    }

    function applyFitmentCookieToSmi() {
      window.SMI = window.SMI || {}

      const fitmentCookieStr = cookieReader('FitmentCookie')
      if (!fitmentCookieStr) {
        return
      }

      let fitmentCookie = {}
      try {
        fitmentCookie = JSON.parse(fitmentCookieStr)
      } catch {
        try {
          fitmentCookie = JSON.parse(decodeURIComponent(fitmentCookieStr))
        } catch {
          fitmentCookie = {}
        }
      }

      window.SMI.raceTypeId = fitmentCookie.RaceTypeId
      window.SMI.ymmYearId = fitmentCookie.Year
      window.SMI.ymmMakeId = fitmentCookie.MakeId
      window.SMI.ymmModelId = fitmentCookie.ModelId
    }

    function pushMisoInteractionData(interactionData) {
      if (!misoApiUrl || !interactionData?.data?.[0]?.type) {
        return
      }

      if (isInternalCustomer()) {
        console.log('Miso interaction not sent because customer is internal')
        return
      }

      try {
        navigator.sendBeacon(misoApiUrl, JSON.stringify(interactionData))
      } catch (error) {
        console.error(error)
      }
    }

    function sendOnExitInteractionData() {
      if (isTrackingBlocked()) {
        return
      }

      if (onExitInterActionDataToSend.length === 0) {
        return
      }

      const timeOnPageEnd = new Date()
      for (let i = 0; i < onExitInterActionDataToSend.length; i++) {
        onExitInterActionDataToSend[i].duration = (timeOnPageEnd - timeOnPageStart) / 1000
      }

      pushMisoInteractionData({ data: onExitInterActionDataToSend })
      onExitInterActionDataToSend.length = 0
    }

    function fireMisoPageView(data = {}) {
      data.time = new Date().toISOString()
      applyFitmentCookieToSmi()

      const userVin = getVin()
      const webUserGuid = getGuid()
      const interactionEntry = { timestamp: data.time }

      if (webUserGuid) {
        interactionEntry.user_id = webUserGuid
      }
      if (userVin) {
        interactionEntry.anonymous_id = userVin
      }

      if (!interactionEntry.user_id && !interactionEntry.anonymous_id) {
        return
      }

      if (window.SMI?.raceTypeId) {
        interactionEntry.context = {
          custom_context: {
            raceTypeId: window.SMI.raceTypeId,
          },
        }
      } else if (window.SMI?.ymmYearId && window.SMI?.ymmMakeId && window.SMI?.ymmModelId) {
        interactionEntry.context = {
          custom_context: {
            yearId: window.SMI.ymmYearId,
            makeId: window.SMI.ymmMakeId,
            modelId: window.SMI.ymmModelId,
          },
        }
      }

      const interactionData = { data: [interactionEntry] }

      switch (data.ptype) {
        case 'product':
          interactionEntry.type = 'product_detail_page_view'
          interactionEntry.product_ids = [data.sku || data.prod_id]
          interactionEntry.product_group_ids = [data.prod_id]
          onExitInterActionDataToSend.push(interactionEntry)
          return
        case 'article':
          interactionEntry.type = 'product_detail_page_view'
          interactionEntry.product_ids = [data.article_id]
          interactionEntry.product_group_ids = []
          onExitInterActionDataToSend.push(interactionEntry)
          return
        case 'category':
          interactionEntry.type = 'category_page_view'
          interactionEntry.category = [data.cat]
          break
        case 'search':
          interactionEntry.type = 'search'
          interactionEntry.search = {
            keywords: data.search_term,
          }
          break
        case 'other':
          interactionEntry.type = 'checkout'
          interactionEntry.product_ids = (data.basket?.items || []).map((item) => item.sku)
          interactionEntry.quantities = (data.basket?.items || []).map((item) => item.quantity)
          break
        default:
          return
      }

      pushMisoInteractionData(interactionData)
    }

    function fireMisoInteraction(interactionData) {
      if (!misoApiUrl || isTrackingBlocked()) {
        return
      }

      if (isInternalCustomer()) {
        console.log('Miso interaction not sent because customer is internal')
        return
      }

      const payload = {
        data: [
          {
            product_ids: [interactionData.prod_id],
            user_id: getGuid(),
            anonymous_id: getVin(),
            timestamp: interactionData.time,
          },
        ],
      }

      if (window.SMI?.raceTypeId) {
        payload.data[0].context = {
          custom_context: {
            raceTypeId: window.SMI.raceTypeId,
          },
        }
      } else if (window.SMI?.ymmYearId && window.SMI?.ymmMakeId && window.SMI?.ymmModelId) {
        payload.data[0].context = {
          custom_context: {
            yearId: window.SMI.ymmYearId,
            makeId: window.SMI.ymmMakeId,
            modelId: window.SMI.ymmModelId,
          },
        }
      }

      switch (interactionData.eventType) {
        case 'quickViewView':
          payload.data[0].type = 'product_detail_page_view'
          payload.data[0].product_ids = [interactionData.sku || interactionData.prod_id]
          payload.data[0].product_group_ids = [interactionData.prod_id]
          break
        default:
          break
      }

      if (payload.data[0].type) {
        const xmlhttp = new XMLHttpRequest()
        xmlhttp.open('POST', misoApiUrl)
        xmlhttp.setRequestHeader('Content-Type', 'application/json')
        xmlhttp.send(JSON.stringify(payload))
      }
    }

    function fireSearchTrackingEvent(data = {}) {
      if (isTrackingBlocked()) {
        return
      }

      data.time = new Date().toISOString()
      if (data?.eventType !== 'addToCart') {
        fireMisoInteraction(data)
      }
    }

    function fireSearchImpression(data) {
      if (!misoApiUrl || isTrackingBlocked()) {
        return
      }

      const payload = {
        data: [
          {
            type: 'impression',
            product_ids: data,
            user_id: getGuid(),
            anonymous_id: getVin(),
            timestamp: new Date().toISOString(),
          },
        ],
      }

      const xmlhttp = new XMLHttpRequest()
      xmlhttp.open('POST', misoApiUrl)
      xmlhttp.setRequestHeader('Content-Type', 'application/json')
      xmlhttp.send(JSON.stringify(payload))
    }

    function handleVisibilityChange() {
      if (document.visibilityState === 'hidden') {
        sendOnExitInteractionData()
      }
    }

    window.pushMisoInteractionData = pushMisoInteractionData
    window.sendOnExitInteractionData = sendOnExitInteractionData
    window.fireMisoPageView = fireMisoPageView
    window.fireSearchPageView = fireMisoPageView
    window.fireSearchTrackingEvent = fireSearchTrackingEvent
    window.fireSearchImpression = fireSearchImpression

    document.addEventListener('visibilitychange', handleVisibilityChange, false)
    window.addEventListener('pagehide', sendOnExitInteractionData, false)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange, false)
      window.removeEventListener('pagehide', sendOnExitInteractionData, false)
    }
  }, [])

  useEffect(() => {
    console.log('voyager page_view')
  }, [])

  return (
    <>
      {/* next/document's <Head> silently strips <title> tags — must use next/head here instead */}
      {title && (
        <Head>
          <title>{title}</title>
        </Head>
      )}
      <Script strategy="beforeInteractive">
        {`
            window.clutchLoadedCallbacks = [];

            function waitForClutch(callback) {
              if (window.clutchLoaded) {
                callback();
              } else {
                window.clutchLoadedCallbacks.push(callback);
              }
            }

            // this fixes TNG's deferred css loading strategy
            // it breaks b/c we parse the tags as React elements instead of raw HTML, so we have to undo the media="print" hack ourselves
            // document.querySelectorAll('link').forEach(function(link) {
            //   link.setAttribute('media', 'all');
            // });
        `}
      </Script>
      {mfeOther && <div dangerouslySetInnerHTML={{ __html: mfeOther }} />}
    </>
  )
}
